import React, { useState, useCallback } from 'react';
import { Tabs, Tab, Box, Grid, Typography, Button, Modal, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Divider } from '@mui/material';
import DomainsTable from "./DomainsTable"
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import { BRAND_TYPE, SAFE_SENDER_CLIENT_CODE, SAFE_SENDER_REDIRECT_URI } from "../../config";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";



function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`reports-tabpanel-${index}`}
            aria-labelledby={`reports-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `reports-tab-${index}`,
        'aria-controls': `reports-tabpanel-${index}`,
    };
}



export default function Deliverability({ data }) {
    const [subTabValue, setSubTabValue] = useState(0);
    const [ipModalOpen, setIpModalOpen] = useState(false);
    const [copiedStates, setCopiedStates] = useState({});
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    
    // List of IPs to display
    const sendingIPs = [
        "23.249.219.118",
        "18.168.104.87",
        "13.42.200.223",
        "3.9.228.40",
        "3.127.7.20",
        "63.178.172.172",
        "13.216.31.253",
        "74.177.142.1"
    ];
    
    const copyToClipboard = useCallback(async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedStates(prev => ({ ...prev, [id]: true }));
            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [id]: false }));
            }, 1500);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }, []);
    
    
    // Get tenancy ID and feature switches
    const { tenancyId, featureSwitches } = useSelector((state) => state.tenancy);

    // Redirect the admin to Microsoft's consent screen for Safe Sender Sync.
    // The auth-service callback (redirect_uri) completes the connect server-side.
    const startSafeSenderConnect = () => {
        const scope = encodeURIComponent("https://graph.microsoft.com/RoleManagement.ReadWrite.Directory openid profile");
        const redirectUri = encodeURIComponent(SAFE_SENDER_REDIRECT_URI);
        window.open(
            `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize?client_id=${SAFE_SENDER_CLIENT_CODE}&response_type=code&redirect_uri=${redirectUri}&response_mode=query&scope=${scope}&prompt=consent&state=${tenancyId}`,
            "_blank"
        );
    };

    // Define all possible tabs with their feature flags
    const allTabs = [
        { 
            label: "Sending Information", 
            component: (
                <>
                    <MainCard content={true} boxShadow={true} border={false}
                        sx={{
                            height: "100%",
                            bgcolor: getCardColor(lightMode),
                            mb: 3
                        }}>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between"
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Typography variant="h5">Sending IPs:</Typography>
                                <Button 
                                    variant="contained" 
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => setIpModalOpen(true)}
                                    size="small"
                                >
                                    View
                                </Button>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "left" }}>
                                <Typography variant="h5">
                                    Header key: X-BPSAT
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "left" }}>
                                <Typography variant="h5">
                                    Header value: 66Y7U72LTS
                                </Typography>
                            </Box>
                        </Box>
                    </MainCard>
                    <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                        <Grid item xs={12} md={12} lg={12}>
                            <DomainsTable />
                        </Grid>
                    </Grid>
                </>
            ),
            featureFlag: 'enableSendingInfo'
        },
        { 
            label: "Microsoft", 
            component: (
                <iframe
                    style={{ width: "100%", height: "80vh", border: "none" }}
                    title={`microsoft`}
                    src={BRAND_TYPE === "censornet" ? 'https://help.clouduss.com/safelisting/microsoft-365-sat-safelisting' : 'https://boxphishsupport.helpdocs.io/article/d1g40drjo3-m-365-allowlisting' }
                />
            ),
            featureFlag: 'enableMicrosoftIntegration' 
        },
        { 
            label: "Google", 
            component: (
                <iframe
                    style={{ width: "100%", height: "80vh", border: "none" }}
                    title={`google`}
                    src={BRAND_TYPE === "censornet" ? 'https://help.clouduss.com/safelisting/google-workspace-sat-safelisting' : 'https://boxphishsupport.helpdocs.io/article/gr96dprgbo-google-workspace'}
                />
            ),
            featureFlag: 'enableGoogleIntegration' 
        },
        { 
            label: "Gateways", 
            component: (
                <iframe
                    style={{ width: "100%", height: "80vh", border: "none" }}
                    title={`gateways`}
                    src={'https://boxphishsupport.helpdocs.io/article/zbvzcguyaw-allow-listing-links'}
                />
            ),
            featureFlag: 'enableGateways',
            brandRestricted: true // Only for boxphish brand
        },
        {
            label: "Safe Sender Sync",
            component: (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography variant="h4">Safe Sender Sync</Typography>
                            </Box>
                            <Box mb={3} mt={1}>
                                <Typography variant="caption">
                                    Automatically add our phishing simulation sending domains to your users' Safe Senders list in Exchange Online, so simulated emails display correctly. Connect once with a Privileged Role Administrator and new starters are covered automatically - this replaces the manual allow-listing steps. Once connected, your users' Safe Senders lists are updated within 24 hours.
                                </Typography>
                            </Box>
                            <Box justifyContent="center" display="flex" flexDirection="column" alignItems="center">
                                <Button
                                    onClick={startSafeSenderConnect}
                                    variant="contained"
                                    color="warning"
                                    sx={{ width: "80%" }}
                                >
                                    Connect to Microsoft
                                </Button>
                            </Box>
                        </MainCard>
                    </Grid>
                </Grid>
            ),
            featureFlag: 'enableSafeSenderSync'
        }
    ];

    // Filter tabs based on feature switches and brand
    const visibleTabs = allTabs.filter(tab => {
        // Check brand restriction
        if (tab.brandRestricted && BRAND_TYPE !== "boxphish") {
            return false;
        }
        // Check feature flag
        if (tab.featureFlag && (!featureSwitches || !featureSwitches[tab.featureFlag])) {
            return false;
        }
        return true;
    });

    const handleSubTabChange = (event, newValue) => {
        setSubTabValue(newValue);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={subTabValue} onChange={handleSubTabChange} aria-label="deliverability tabs">
                    {visibleTabs.map((tab, index) => (
                        <Tab key={index} label={tab.label} {...a11yProps(index)} />
                    ))}
                </Tabs>
            </Box>
            {visibleTabs.map((tab, index) => (
                <TabPanel key={index} value={subTabValue} index={index}>
                    {tab.component}
                </TabPanel>
            ))}
            
            {/* IP Display Modal */}
            <Modal
                open={ipModalOpen}
                onClose={() => setIpModalOpen(false)}
                aria-labelledby="ip-modal-title"
                aria-describedby="ip-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 500,
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.borderRadius || 1,
                        p: 0,
                        outline: 'none'
                    }}
                >
                    <Box 
                        sx={{ 
                            display: "flex", 
                            justifyContent: "space-between", 
                            alignItems: "center",
                            p: 3,
                            pb: 2
                        }}
                    >
                        <Typography id="ip-modal-title" variant="h4">
                            Sending IP Addresses
                        </Typography>
                        <IconButton 
                            onClick={() => setIpModalOpen(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ p: 3, pt: 2 }}>
                        <List sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', borderRadius: 1, p: 1 }}>
                            {sendingIPs.map((ip, index) => (
                                <React.Fragment key={ip}>
                                    <ListItem 
                                        sx={{ 
                                            py: 1.5,
                                            '&:hover': {
                                                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                                                borderRadius: 1
                                            }
                                        }}
                                    >
                                        <ListItemText 
                                            primary={ip}
                                            primaryTypographyProps={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.95rem'
                                            }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton 
                                                edge="end" 
                                                onClick={() => copyToClipboard(ip, ip)}
                                                size="small"
                                            >
                                                {copiedStates[ip] ? 
                                                    <CheckIcon fontSize="small" color="success" /> : 
                                                    <ContentCopyIcon fontSize="small" />
                                                }
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < sendingIPs.length - 1 && <Divider variant="middle" />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}